import { useState, useEffect, useCallback } from 'react';
import type {
  BillingSummary,
  TenantSubscription,
  TenantUsage,
  TenantInvoice,
  CreateSubscriptionDto,
  UpdateSubscriptionDto,
  MarkInvoicePaidDto,
} from '@yawi/types';
import { useServices, useToast } from '@/app/providers';

export function useTenantBilling(tenantId: string) {
  const { billingRepository } = useServices();
  const { showToast } = useToast();

  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [invoices, setInvoices] = useState<TenantInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [summaryData, invoicesData] = await Promise.all([
        billingRepository.getTenantBillingSummary(tenantId),
        billingRepository.getTenantInvoices(tenantId),
      ]);
      setSummary(summaryData);
      setInvoices(invoicesData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load billing data';
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, billingRepository, showToast]);

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId, loadData]);

  const createSubscription = async (dto: CreateSubscriptionDto) => {
    try {
      await billingRepository.createSubscription(tenantId, dto);
      showToast('success', 'Subscription created successfully');
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create subscription';
      showToast('error', message);
      throw err;
    }
  };

  const updateSubscription = async (dto: UpdateSubscriptionDto) => {
    try {
      await billingRepository.updateSubscription(tenantId, dto);
      showToast('success', 'Subscription updated successfully');
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update subscription';
      showToast('error', message);
      throw err;
    }
  };

  const cancelSubscription = async () => {
    try {
      await billingRepository.cancelSubscription(tenantId);
      showToast('success', 'Subscription cancelled');
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel subscription';
      showToast('error', message);
      throw err;
    }
  };

  const generateInvoice = async (period?: string) => {
    try {
      await billingRepository.generateInvoice(tenantId, period);
      showToast('success', 'Invoice generated successfully');
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate invoice';
      showToast('error', message);
      throw err;
    }
  };

  const markInvoicePaid = async (invoiceId: string, dto?: MarkInvoicePaidDto) => {
    try {
      await billingRepository.markInvoicePaid(invoiceId, dto);
      showToast('success', 'Invoice marked as paid');
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark invoice as paid';
      showToast('error', message);
      throw err;
    }
  };

  const cancelInvoice = async (invoiceId: string) => {
    try {
      await billingRepository.cancelInvoice(invoiceId);
      showToast('success', 'Invoice cancelled');
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel invoice';
      showToast('error', message);
      throw err;
    }
  };

  return {
    summary,
    invoices,
    isLoading,
    refetch: loadData,
    createSubscription,
    updateSubscription,
    cancelSubscription,
    generateInvoice,
    markInvoicePaid,
    cancelInvoice,
  };
}
